// vid-reverse-loop
import { onDestroy, onMount, onView } from "@/modules/_";
import { reduced } from "@lib/gsap";
import { Raf } from "@lib/subs";
import { handleEditor } from "@webflow/detect-editor";

// Ping-pongs a video: plays to the end, runs back to the start, plays again.
//
//   [data-module="vid-reverse-loop"]
//     [vid-reverse-loop="video"]     <- the <video> (falls back to the first
//                                       one inside, or the element itself)
//
//   data-loop-start="0.3333"  where the forward leg begins
//   data-loop-end="1.6"       timestamp of the last frame to include
//   data-loop-fps="15"        the clip's frame rate
//   data-loop-rate="0.8"      speed of the backwards leg, 1 = same as forward
//   data-loop-hold="0.15"     extra seconds to sit still at each end
//
// start/end trim exported padding: a clip handed over as "frames 71-95" often
// opens on a held pose, and every cycle would sit in it twice. fps lets each
// turnaround step off the boundary frame, which would otherwise render twice
// in a row and read as a stutter.
//
// No browser implements a negative playbackRate, so the backwards leg is
// scrubbed — a seek per frame. Only that leg pays it: forward is ordinary
// playback, and the ticker isn't subscribed while it runs unless a trimmed end
// means `ended` won't fire. Scrubbing is only as smooth as the encode, since a
// seek decodes from the previous keyframe; a stretch without one stutters
// however this is written.
//
// `loop` is dropped on mount: left on, the video restarts instead of ending.

const DEFAULT_RATE = 1;
const DEFAULT_HOLD = 0;
// A backgrounded tab pauses the ticker; uncapped, the resuming frame would
// carry the whole gap and rewind the clip in one step.
const MAX_FRAME_SECONDS = 0.1;
const EDGE_EPSILON = 0.005;

type Phase = "forward" | "reverse" | "hold";

function num(value: string | undefined, fallback: number) {
  const parsed = parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
}

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const tagged = element.querySelector('[vid-reverse-loop="video"]');
  const candidate =
    tagged ??
    element.querySelector("video") ??
    (element instanceof HTMLVideoElement ? element : null);
  if (!(candidate instanceof HTMLVideoElement)) return;
  const video: HTMLVideoElement = candidate;

  const rate = Math.max(0.05, num(dataset.loopRate, DEFAULT_RATE));
  const hold = Math.max(0, num(dataset.loopHold, DEFAULT_HOLD));
  const fps = Math.max(0, num(dataset.loopFps, 0));
  // Zero unless the rate is known: a guessed offset would drop a real frame.
  const frameStep = fps > 0 ? 1 / fps : 0;
  const wantStart = Math.max(0, num(dataset.loopStart, 0));
  const wantEnd = num(dataset.loopEnd, Infinity);

  // Resolved against the real duration once metadata lands.
  let startTime = 0;
  let endTime = 0;
  // Forward runs one frame past the last kept frame, so it gets its full time
  // on screen before the turn rather than flashing.
  let forwardStop = 0;
  // `ended` only fires at the true end of the file; a trimmed end needs a poll.
  let watchEnd = false;
  let ready = false;

  let phase: Phase = "forward";
  let holdNext: Phase = "reverse";
  let holdFor = 0;
  let holdElapsed = 0;

  let offRaf: (() => void) | null = null;
  let lastT = 0;
  let inView = false;
  let destroyed = false;

  const hadLoop = video.hasAttribute("loop");

  function resolve() {
    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) return false;

    endTime = Math.min(wantEnd, duration);
    startTime = Math.max(
      0,
      Math.min(wantStart, Math.max(0, endTime - frameStep)),
    );
    forwardStop = Math.min(duration, endTime + frameStep);
    watchEnd = forwardStop < duration - EDGE_EPSILON;
    ready = true;
    return true;
  }

  /* Ticking */

  function startTicking() {
    if (offRaf || destroyed) return;
    lastT = performance.now();
    offRaf = Raf.add(tick);
  }

  function stopTicking() {
    offRaf?.();
    offRaf = null;
  }

  function tick() {
    const now = performance.now();
    let dt = (now - lastT) / 1000;
    lastT = now;
    if (dt > MAX_FRAME_SECONDS) dt = MAX_FRAME_SECONDS;

    if (phase === "hold") {
      holdElapsed += dt;
      if (holdElapsed < holdFor) return;
      begin(holdNext);
      return;
    }

    if (phase === "forward") {
      if (watchEnd && video.currentTime >= forwardStop - EDGE_EPSILON) {
        turn("reverse");
      }
      return;
    }

    const next = video.currentTime - dt * rate;
    if (next <= startTime + EDGE_EPSILON) {
      video.currentTime = startTime;
      turn("forward");
      return;
    }
    video.currentTime = next;
  }

  /* Legs */

  function begin(next: Phase) {
    phase = next;

    if (next === "forward") {
      // Step past the frame the reverse leg finished on.
      video.currentTime = Math.min(endTime, startTime + frameStep);
      void video.play().catch(() => {});
      if (watchEnd) startTicking();
      else stopTicking();
      return;
    }

    // Playback has to be off before currentTime is driven by hand.
    video.pause();
    video.currentTime = Math.max(startTime, endTime - frameStep);
    startTicking();
  }

  // The bottom waits at least a frame: the forward leg leaves its last frame up
  // for a full frame of playback, so the backwards leg has to match or the two
  // ends of the cycle run at different speeds.
  function turn(next: Phase) {
    const minimum = next === "forward" ? frameStep / rate : 0;
    holdFor = Math.max(hold, minimum);

    if (holdFor <= 0) {
      begin(next);
      return;
    }

    phase = "hold";
    holdNext = next;
    holdElapsed = 0;
    video.pause();
    startTicking();
  }

  function onEnded() {
    if (destroyed || !inView || !ready) return;
    turn("reverse");
  }

  function onMeta() {
    if (destroyed || !resolve()) return;
    video.currentTime = startTime;
    if (inView) begin("forward");
  }

  function onMetaReduced() {
    if (destroyed || !resolve()) return;
    video.currentTime = startTime;
  }

  /* Lifecycle */

  onMount(() => {
    if (handleEditor()) return;

    video.removeAttribute("loop");
    video.loop = false;
    // Autoplay is only granted to muted video.
    video.muted = true;
    video.playsInline = true;

    video.addEventListener("ended", onEnded);

    if (reduced) {
      video.pause();
      video.removeAttribute("autoplay");
      if (!resolve()) video.addEventListener("loadedmetadata", onMetaReduced);
      else video.currentTime = startTime;
      return;
    }

    if (resolve()) video.currentTime = startTime;
    else video.addEventListener("loadedmetadata", onMeta);

    onView(element, {
      autoStart: true,
      callback: ({ isIn }) => {
        if (destroyed) return;
        inView = isIn;
        if (!isIn) {
          video.pause();
          stopTicking();
          return;
        }
        // Metadata still pending: onMeta starts it.
        if (!ready) return;
        // Resume the interrupted leg rather than restarting the cycle.
        if (phase === "forward") {
          void video.play().catch(() => {});
          if (watchEnd) startTicking();
        } else {
          startTicking();
        }
      },
    });

    onDestroy(() => {
      destroyed = true;
      stopTicking();
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("loadedmetadata", onMetaReduced);
      video.pause();
      if (hadLoop) {
        video.loop = true;
        video.setAttribute("loop", "");
      }
    });
  });
}
